"use client";

import type { CSSProperties, HTMLAttributes, ReactNode } from "react";
import { motion, useReducedMotion, type HTMLMotionProps } from "motion/react";
import TiltCard from "@/components/ui/TiltCard";
import { useLookSystem } from "@/components/motion/LookMotion";

type CardTag = "div" | "article" | "li";
type DataAttributes = {
  [key: `data-${string}`]: string | number | boolean | undefined;
};

/** A card that glides up as it appears (Kortene glider ind) and tilts under the pointer (Hældning), neither for
 *  visitors who prefer less motion. `as` keeps the card's own element, so a grid or list keeps its direct children.
 *  On the look system the card is a reveal and card marker instead (src/styles/motion.css), so its section's motion
 *  level decides how it appears, and TiltCard holds still where the level or the visitor asks for it. Without `reveal`
 *  it is only a card there, which moves with what holds it (a carousel that reveals as one). */
export default function CardMotion({
  flyIn,
  tilt = false,
  reveal = true,
  index,
  as: Tag = "div",
  children,
  ...rest
}: {
  flyIn: boolean;
  tilt?: boolean;
  reveal?: boolean;
  index: number;
  as?: CardTag;
  children: ReactNode;
} & HTMLAttributes<HTMLElement> &
  DataAttributes) {
  const reduced = useReducedMotion();
  if (useLookSystem()) {
    // The section's intro is its first reveal, so the cards stagger after it.
    const staggered = {
      ...rest.style,
      "--re-reveal-index": index + 1,
    } as CSSProperties;
    return (
      <Tag
        {...rest}
        data-re-reveal={reveal ? "" : undefined}
        data-re-card=""
        style={reveal ? staggered : rest.style}
      >
        {tilt ? <TiltCard>{children}</TiltCard> : children}
      </Tag>
    );
  }
  const inner = tilt && !reduced ? <TiltCard>{children}</TiltCard> : children;
  if (!flyIn || reduced) return <Tag {...rest}>{inner}</Tag>;
  const Motion = motion[Tag] as typeof motion.div;
  return (
    <Motion
      {...(rest as HTMLMotionProps<"div">)}
      data-card-motion=""
      initial={{ opacity: 0, y: 48 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "100px" }}
      transition={{ duration: 0.4, delay: (index % 6) * 0.05 }}
    >
      {inner}
    </Motion>
  );
}
